import { round2 } from '../catalog/catalog';
import { DEFAULT_CURRENCY_CODE, formatCurrency } from '../money';
import { bomCostCents, dealValueFloorCents, floorMessage, type BomLineLike } from './dealPricing';

/** 🔒 A Quote's total under its Deal's floor, and the arithmetic the warning states (decisions 0034, 1629). Integer cents. */
export interface DocumentFloorBreach {
  totalCents: number;
  floorCents: number;
  costCents: number;
  /** The floor less the total, which an acknowledgement's `shortfall` records. */
  shortfallCents: number;
  /** The margin the floor implies over the cost, the floor being summed line by line at each line's own margin (decision 0048). */
  marginPercent: number;
  /** The sentence the warning, the routing and the refusal all read, through `floorMessage`. */
  message: string;
}

/**
 * 🔒 The document floor (decision 1629): a total at or above the floor, or a
 * floor of nothing, raises nothing. Below it, the breach carries the shortfall
 * and the exact arithmetic, never only a verdict.
 */
export function documentFloorBreach(input: { totalCents: number; floorCents: number; costCents: number }): DocumentFloorBreach | null {
  const { totalCents, floorCents, costCents } = input;
  if (floorCents <= 0 || totalCents >= floorCents) return null;
  const marginPercent = costCents > 0 ? round2((1 - costCents / floorCents) * 100) : 0;
  return {
    totalCents,
    floorCents,
    costCents,
    shortfallCents: floorCents - totalCents,
    marginPercent,
    message: floorMessage('document', totalCents / 100, floorCents / 100, costCents / 100, marginPercent),
  };
}

/** 🔒 One Quote line priced under the floor of the BOM rows it names (T920; decision 1695). Integer cents. */
export interface LineFloorBreach extends DocumentFloorBreach {
  lineId: string;
  lineNumber: number;
  /** The line as the sheet names it, its description. */
  label: string;
}

const lineName = (lineNumber: number, label: string): string => `Line ${lineNumber}${label.trim() ? ` (${label.trim()})` : ''}`;

/**
 * 🔒 A Quote line's floor (piece 18; T920, decision 1695): the line floors of
 * the BOM rows its `bomLineIds` names, each at its own minimum margin, summed.
 * A line naming no row has no floor, and a line at or above its floor raises
 * nothing. The amount is the line's own, net of its discount and before tax.
 *
 * 🔒 On a Deal carrying no bill of materials the line is held to its own Item
 * instead (decision 2511): its quantity at the Item's purchase price and minimum
 * margin, and the arithmetic names the Item's cost rather than the list's.
 */
export function lineFloorBreach(input: { lineId: string; lineNumber: number; label: string; amountCents: number; bomLines: readonly BomLineLike[]; costBasis?: 'bom' | 'item' }): LineFloorBreach | null {
  if (input.bomLines.length === 0) return null;
  const floorCents = dealValueFloorCents(input.bomLines);
  const costCents = bomCostCents(input.bomLines);
  if (floorCents <= 0 || input.amountCents >= floorCents) return null;
  const marginPercent = costCents > 0 ? round2((1 - costCents / floorCents) * 100) : 0;
  const money = (cents: number): string => formatCurrency({ amount: cents, currencyCode: DEFAULT_CURRENCY_CODE });
  return {
    lineId: input.lineId,
    lineNumber: input.lineNumber,
    label: input.label,
    totalCents: input.amountCents,
    floorCents,
    costCents,
    shortfallCents: floorCents - input.amountCents,
    marginPercent,
    message: `${lineName(input.lineNumber, input.label)} at ${money(input.amountCents)} is below its line floor of ${money(floorCents)} (${input.costBasis === 'item' ? 'Item cost' : 'BOM cost'} ${money(costCents)} at a ${marginPercent}% minimum margin, ${money(costCents)} ÷ ${(1 - marginPercent / 100).toFixed(2)}).`,
  };
}

/** 🔨 A reason covers the breach while the shortfall is no larger than the one it acknowledged (decision 1629). */
export function floorAcknowledged(shortfallCents: number, acknowledgedShortfallCents: readonly number[]): boolean {
  return acknowledgedShortfallCents.some((acknowledged) => acknowledged >= shortfallCents);
}

/**
 * 🔒 The breach as a viewer the cost rule refuses reads it (decisions 0276,
 * 1629): the total, the floor and the shortfall, and never the cost, the margin
 * or the arithmetic that reached the floor. The routing's warning and the
 * stored approval reason read this form for every viewer, because whoever opens
 * the approval later reads the text the routing wrote.
 */
export type CostlessFloorBreach<T extends DocumentFloorBreach = DocumentFloorBreach> = Omit<T, 'costCents' | 'marginPercent'>;

export function floorWithoutCost<T extends DocumentFloorBreach>(breach: T): CostlessFloorBreach<T> {
  const rest: Partial<T> = { ...breach };
  delete rest.costCents;
  delete rest.marginPercent;
  const money = (cents: number): string => formatCurrency({ amount: cents, currencyCode: DEFAULT_CURRENCY_CODE });
  // 🔒 A line's breach names its line the same way without the cost (T920; decision 1695).
  if ('lineNumber' in breach && typeof breach.lineNumber === 'number') {
    const named = breach as unknown as LineFloorBreach;
    return { ...(rest as CostlessFloorBreach<T>), message: `${lineName(named.lineNumber, named.label)} at ${money(breach.totalCents)} is below its line floor of ${money(breach.floorCents)}, ${money(breach.shortfallCents)} short.` };
  }
  return { ...(rest as CostlessFloorBreach<T>), message: `Quote total ${money(breach.totalCents)} is below the document floor of ${money(breach.floorCents)}, ${money(breach.shortfallCents)} short.` };
}

/**
 * 🔒 The words the floor's refusal ends with, on a send and on an approval (decision 1629). They are for the person
 * reading the refusal; the approval queue does not read them (T1235).
 */
export const FLOOR_REFUSAL_TAIL = "Acknowledge the floor with a reason on the Quote's page before it is approved or sent.";

/**
 * 🔒 The code the floor's refusal carries in its body beside the sentence (T1235). The approval queue reads it to know an
 * Approve was refused for the floor and opens the override window (T1195; decision 1861), so no wording is matched.
 */
export const FLOOR_REFUSAL_CODE = 'floorNotAcknowledged';

/** Whether a refusal's body is the floor's, so the queue asks for the override rather than printing it (decision 1861). */
export function isFloorRefusal(body: unknown): boolean {
  return typeof body === 'object' && body !== null && (body as { code?: unknown }).code === FLOOR_REFUSAL_CODE;
}
